import React from 'react';
import { Gift, Sparkles, Star, Brain, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import AdminSectionHeader from '@/components/layout/AdminSectionHeader';
import { FeatureCards } from '@/components/whats-new/FeatureCards';
import { DevelopmentHighlights } from '@/components/whats-new/DevelopmentHighlights';
import { getFeatureStats } from '@/data/whatsNewFeatures';

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const WhatsNew: React.FC = () => {
  const { totalFeatures, featuresByType, featuresByCategory } = getFeatureStats();

  return (
    <div className="p-6 space-y-6">
      <AdminSectionHeader
        title="What's New"
        description="Discover the latest features and improvements to your Hivetivity experience"
        icon={Gift}
        iconClassName="text-primary"
        iconBgClassName="bg-primary/10"
      />

      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Development Statistics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalFeatures}</p>
                  <p className="text-xs text-muted-foreground">Features Released</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Star className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{featuresByType.feature || 0}</p>
                  <p className="text-xs text-muted-foreground">New Features</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{featuresByType.enhancement || 0}</p>
                  <p className="text-xs text-muted-foreground">Enhancements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Brain className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{featuresByCategory.ai || 0}</p>
                  <p className="text-xs text-muted-foreground">AI Features</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature Cards */}
        <FeatureCards 
          mobile={false}
          animationDelay={0.05}
          gridClassName="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        />

        {/* Development Timeline Summary */}
        <DevelopmentHighlights adminView={true} />
      </motion.div>
    </div>
  );
};

export default WhatsNew;